import { MergedSearchResult, shuffleArray } from '../../types/overlay-adaptors';

interface BaseAdaptor {
  name: string;
  displayName: string;
}

interface SearchAdaptor<TItem, TParams> extends BaseAdaptor {
  search: (params: TParams, config?: Record<string, any>) => Promise<{
    items: TItem[];
    totalCount: number;
    hasMore?: boolean;
  }>;
}

interface TemplateAdaptor<TItem> extends BaseAdaptor {
  getTemplates: (params?: any, config?: Record<string, any>) => Promise<{
    items: TItem[];
    totalCount: number;
    hasMore?: boolean;
  }>;
}

/**
 * Generic search function that merges results from multiple adaptors
 */
export async function searchAcrossAdaptors<TItem, TParams>(
  adaptors: SearchAdaptor<TItem, TParams>[],
  params: TParams,
  config?: Record<string, any>,
  options: {
    shuffle?: boolean;
    errorMessage?: string;
  } = {}
): Promise<MergedSearchResult<TItem>> {
  const { shuffle = true, errorMessage = 'Error searching' } = options;

  if (adaptors.length === 0) {
    return {
      items: [],
      totalCount: 0,
      hasMore: false,
      sourceResults: []
    };
  }

  // Execute all adaptor searches in parallel
  const searchPromises = adaptors.map(async (adaptor) => {
    try {
      const result = await adaptor.search(params, config);
      return {
        adaptor,
        result,
        error: null
      };
    } catch (error) {
      console.error(`${errorMessage} with ${adaptor.name}:`, error);
      return {
        adaptor,
        result: { items: [], totalCount: 0, hasMore: false },
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  });

  const searchResults = await Promise.all(searchPromises);

  // Merge and attribute results
  const mergedItems: Array<TItem & { _source: string; _sourceDisplayName: string }> = [];
  const sourceResults: MergedSearchResult<TItem>['sourceResults'] = [];

  searchResults.forEach(({ adaptor, result, error }) => {
    // Add source attribution to each item
    const attributedItems = result.items.map(item => ({
      ...item,
      _source: adaptor.name,
      _sourceDisplayName: adaptor.displayName
    }));

    mergedItems.push(...attributedItems);

    sourceResults.push({
      adaptorName: adaptor.name,
      adaptorDisplayName: adaptor.displayName,
      itemCount: result.items.length,
      hasMore: result.hasMore || false,
      ...(error && { error })
    });
  });

  // Shuffle merged results to mix sources if requested
  const finalItems = shuffle ? shuffleArray(mergedItems) : mergedItems;

  return {
    items: finalItems,
    totalCount: mergedItems.length,
    hasMore: sourceResults.some(s => s.hasMore),
    sourceResults
  };
}

/**
 * Generic template fetching function that merges results from multiple adaptors
 */
export async function getTemplatesAcrossAdaptors<TItem>(
  adaptors: TemplateAdaptor<TItem>[],
  params?: any,
  config?: Record<string, any>,
  options: {
    shuffle?: boolean;
    errorMessage?: string;
    enablePagination?: boolean;
  } = {}
): Promise<MergedSearchResult<TItem>> {
  const { shuffle = true, errorMessage = 'Error fetching templates', enablePagination = false } = options;

  if (adaptors.length === 0) {
    return {
      items: [],
      totalCount: 0,
      hasMore: false,
      sourceResults: []
    };
  }

  // Execute all adaptor template fetches in parallel
  const templatePromises = adaptors.map(async (adaptor) => {
    try {
      const result = await adaptor.getTemplates(params, config);
      return {
        adaptor,
        result,
        error: null
      };
    } catch (error) {
      console.error(`${errorMessage} from ${adaptor.name}:`, error);
      return {
        adaptor,
        result: { items: [], totalCount: 0, hasMore: false },
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  });

  const templateResults = await Promise.all(templatePromises);

  // Merge and attribute results
  const mergedItems: Array<TItem & { _source: string; _sourceDisplayName: string }> = [];
  const sourceResults: MergedSearchResult<TItem>['sourceResults'] = [];

  templateResults.forEach(({ adaptor, result, error }) => {
    // Add source attribution to each item
    const attributedItems = result.items.map(item => ({
      ...item,
      _source: adaptor.name,
      _sourceDisplayName: adaptor.displayName
    }));

    mergedItems.push(...attributedItems);

    sourceResults.push({
      adaptorName: adaptor.name,
      adaptorDisplayName: adaptor.displayName,
      itemCount: result.items.length,
      hasMore: enablePagination ? (result.hasMore || false) : false,
      ...(error && { error })
    });
  });

  // Shuffle merged results to mix sources if requested
  const finalItems = shuffle ? shuffleArray(mergedItems) : mergedItems;

  return {
    items: finalItems,
    totalCount: mergedItems.length,
    hasMore: enablePagination ? sourceResults.some(s => s.hasMore) : false,
    sourceResults
  };
}
